import { confirmCost } from "@/components/cost-confirm";
import { useState, type ReactNode } from "react";

import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { WriterMd } from "@/components/writer-md";
import { AgentGroup, AgentOption } from "@/components/agent-ui";
import { Link } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useTakvimdeVar } from "@/hooks/use-takvim";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Lightbulb, Globe, RefreshCw, Loader2, CalendarPlus, Check, FileText,
  TrendingUp, Search, Target, ExternalLink, Sparkles,
} from "lucide-react";

// PERSONA FORMU YARDIMCILARI — MODÜL SEVİYESİNDE OLMAK ZORUNDA (kritik hata dersi,
// 2026-08-21): FL render içinde tanımlıyken her tuşta yeni bileşen tipi oluşuyor,
// React alt ağacı söküp yeniden kuruyor ve input HER HARFTE odağı kaybediyordu.
const PERSONA_INPUT = "text-sm w-full px-3 py-2 rounded-lg border outline-none bg-white border-black/10 text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500";
const PERSONA_TEXTAREA = PERSONA_INPUT + " resize-y";
// Alan başlığı: placeholder dolunca da hangi alan olduğu görünsün
const FL = ({ l, children }: { l: string; children: ReactNode }) => (
  <div>
    <div className="mb-1 text-theme-xs font-semibold uppercase tracking-wide text-brand-500">{l}</div>
    {children}
  </div>
);

type OppKw = { keyword: string; impressions: number; position: number; clicks: number; volume: number; competition: number };
type OppRec = {
  title: string; type: "new" | "expand"; relatedPageUrl: string | null; clusterName: string | null;
  rationale: string; priority: "high" | "medium" | "low"; estimatedImpressions: number; estimatedVolume: number;
  targetKeywords: OppKw[];
};
type OppResp = { gscConnected: boolean; generatedAt: string | null; recommendations: OppRec[]; meta: { candidateCount?: number; pageCount?: number; gscSite?: string } };

type PosItem = { question: string; aiSearchVolume: number; position: number; totalSources: number; url: string; above: { domain: string; title: string }[]; tips: string[] };
type PosResp = { configured: boolean; generatedAt: string | null; items: PosItem[] };

// ── Yazar Motoru tipleri (backend writer_briefs ile eşlenik) ──
type WriterBrief = {
  title: string; targetKeyword: string; volume: number; intentKind?: string; clusterName: string | null;
  supportingKeywords: string[]; outline: { h2: string; h3: string[] }[]; rationale: string;
};
type ImageBriefT = { position: string; prompt: string; altText: string; aspectRatio: string; placeholder: string; imageUrl?: string | null; imageError?: string | null };
type WriterResultT = {
  title: string; metaDescription: string; slug: string; content: string;
  imageBriefs: ImageBriefT[]; toneUsed: string;
  linkPlan: { anchor: string; url: string; reason: string }[];
  serpCompetitors: { url: string; title: string; strengths: string[] }[];
  geoTactics: string[]; schemaSuggestion: string;
  audit: { score: number; passed: boolean; fixedIssues: number; notes: string[] } | null;
  wordCount: number;
};
type WriterRow = {
  id: number; brief: WriterBrief; result: WriterResultT | null; status: string;
  createdAt: string; writtenAt: string | null; running?: boolean; runError?: string | null;
};
// İçerik Robotu ile AYNI dil seti (kullanıcı isteği — tüm pazarlar)
const WRITER_MARKETS = [
  { k: "tr", l: "🇹🇷 TR" }, { k: "en", l: "🇺🇸 EN" }, { k: "en-gb", l: "🇬🇧 UK" }, { k: "de", l: "🇩🇪 DE" },
  { k: "fr", l: "🇫🇷 FR" }, { k: "es", l: "🇪🇸 ES" }, { k: "it", l: "🇮🇹 IT" }, { k: "ar", l: "🇸🇦 AR" },
  { k: "ru", l: "🇷🇺 RU" }, { k: "nl", l: "🇳🇱 NL" }, { k: "pt", l: "🇵🇹 PT" }, { k: "pt-br", l: "🇧🇷 BR" },
  { k: "pl", l: "🇵🇱 PL" }, { k: "sv", l: "🇸🇪 SV" }, { k: "el", l: "🇬🇷 EL" }, { k: "he", l: "🇮🇱 HE" },
  { k: "ja", l: "🇯🇵 JA" }, { k: "ko", l: "🇰🇷 KO" }, { k: "zh", l: "🇹🇼 ZH" }, { k: "hi", l: "🇮🇳 HI" },
  { k: "id", l: "🇮🇩 ID" }, { k: "az", l: "🇦🇿 AZ" },
];
const TONES = [
  { k: "otomatik", l: "🤖 Otomatik", d: "Motor, niyet+sektöre göre seçer" },
  { k: "site-tonu", l: "🪞 Site Tonu", d: "Mevcut içeriklerinle aynı kalem" },
  { k: "profesyonel", l: "👔 Profesyonel", d: "Net, kurumsal, güven veren" },
  { k: "samimi", l: "😊 Samimi", d: "Sıcak, arkadaşça sen/siz dili" },
  { k: "uzman-otoriter", l: "🎓 Uzman", d: "E-E-A-T güçlü, kanıta dayalı" },
  { k: "egitici", l: "📚 Eğitici", d: "Adım adım öğreten" },
  { k: "satis-odakli", l: "💼 Satış Odaklı", d: "Faydacı, CTA'lı, ikna edici" },
];


const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };

const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => (u || "").replace(/^https?:\/\/[^/]+/, "").replace(/#:~:.*$/, "").replace(/\/$/, "") || "/";
const compColor = (c: number) => (c < 33 ? C.green : c < 66 ? C.amber : C.red);
const compLabel = (c: number) => (c < 33 ? "düşük" : c < 66 ? "orta" : "yüksek");

export default function ContentOpportunities() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();

  const tab = "yazar" as string; // sekmeler kaldırıldı — sayfa yalnız Yazar Motoru
  // ── PERSONA FORM STİLLERİ + ALAN BAŞLIĞI (modül dışına alınamayan tek şey state) ──
  // ── YAZAR MOTORU: brief havuzu (Robot'tan gelenler) + üretim ayarları ──
  const [tone, setTone] = useState("otomatik");
  const [writerModel, setWriterModel] = useState<"opus" | "fable">("fable"); // kullanıcı tercihi: varsayılan Fable 5
  const [narrator, setNarrator] = useState<"marka-biz" | "notr-uculcu">("notr-uculcu");
  // Hedef Kitle serbest metni KALDIRILDI — yerini Hedef Persona sistemi aldı.
  // Varsayılan 600 (kullanıcı isteği: "500-700 civarı yazmamız lazım"); seçim hatırlanır
  const [wordTarget, setWordTargetRaw] = useState<number>(() => { const v = localStorage.getItem("yazar-kelime-hedefi"); return v !== null && Number.isFinite(Number(v)) ? Number(v) : 600; });
  const setWordTarget = (v: number) => { setWordTargetRaw(v); localStorage.setItem("yazar-kelime-hedefi", String(v)); };
  const [imageCount, setImageCount] = useState(3);
  const [wMarket, setWMarket] = useState("tr");
  const [showAllLangs, setShowAllLangs] = useState(false);

  // ── HEDEF PERSONA (GEO): domain başına çoklu persona; seçilen yazara gömülür ──
  type PersonaItem = {
    id: number; name: string; source: string; market?: string | null;
    data: { summary: string; demographics: string; role: string; goals: string[]; painPoints: string[]; questions: string[]; objections: string[]; decisionFactors: string[]; searchBehavior: string; languageStyle: string };
  };
  const [personaIds, setPersonaIds] = useState<Set<number>>(new Set()); // ÇOKLU seçim
  const [personaPanel, setPersonaPanel] = useState(false);
  const [personaBusy, setPersonaBusy] = useState(false);
  const [personaErr, setPersonaErr] = useState<string | null>(null);
  const [pAudience, setPAudience] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null); // düzenlenen persona (null = yeni)
  const emptyForm = { name: "", summary: "", demographics: "", role: "", questions: "", painPoints: "", objections: "", decisionFactors: "", searchBehavior: "", languageStyle: "", market: "" };
  const [pForm, setPForm] = useState({ ...emptyForm });
  const setF = (k: keyof typeof emptyForm, v: string) => setPForm((f) => ({ ...f, [k]: v }));
  const togglePersona = (id: number) => setPersonaIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const { data: personas } = useQuery({
    queryKey: ["personas", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<PersonaItem[]> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/personas`); return r.ok ? await r.json() : []; } catch { return []; }
    },
  });
  const generatePersonaAuto = async () => {
    setPersonaBusy(true); setPersonaErr(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/personas/generate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ audience: pAudience || undefined, language: wMarket }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? "Persona üretilemedi");
      setPersonaIds((s) => new Set(s).add(data.id)); setPersonaPanel(false); setPAudience("");
      queryClient.invalidateQueries({ queryKey: ["personas", activeDomainId] });
    } catch (e) { setPersonaErr((e as Error).message); } finally { setPersonaBusy(false); }
  };
  // "Alanları Otomatik Doldur" (kullanıcı isteği): ad/özet girildiyse kalan alanları
  // AI doldurur — KAYDETMEZ, kullanıcı düzenleyip kendisi kaydeder. Dolu alan ezilmez.
  const alanlariDoldur = async () => {
    if (!pForm.name.trim() && !pForm.summary.trim()) { setPersonaErr("Önce persona adını ya da özetini yaz — kalanını ona göre doldurayım."); return; }
    setPersonaBusy(true); setPersonaErr(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/personas/generate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ audience: [pForm.name, pForm.summary].filter(Boolean).join(". "), language: pForm.market.trim() || wMarket, sadeceVeri: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? "Doldurulamadı");
      const d = data.data as PersonaItem["data"];
      setPForm((f) => ({
        ...f,
        name: f.name.trim() || String(data.name ?? ""),
        summary: f.summary.trim() || (d.summary ?? ""),
        demographics: f.demographics.trim() || (d.demographics ?? ""),
        role: f.role.trim() || (d.role ?? ""),
        questions: f.questions.trim() || (d.questions ?? []).join("\n"),
        painPoints: f.painPoints.trim() || (d.painPoints ?? []).join("\n"),
        objections: f.objections.trim() || (d.objections ?? []).join("\n"),
        decisionFactors: f.decisionFactors.trim() || (d.decisionFactors ?? []).join("\n"),
        searchBehavior: f.searchBehavior.trim() || (d.searchBehavior ?? ""),
        languageStyle: f.languageStyle.trim() || (d.languageStyle ?? ""),
      }));
    } catch (e) { setPersonaErr((e as Error).message); } finally { setPersonaBusy(false); }
  };
  const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const savePersonaManual = async () => {
    if (!pForm.name.trim()) { setPersonaErr("Persona adı gerekli"); return; }
    setPersonaBusy(true); setPersonaErr(null);
    try {
      const payload = { name: pForm.name, market: pForm.market.trim() || undefined, data: {
        summary: pForm.summary, demographics: pForm.demographics, role: pForm.role,
        questions: lines(pForm.questions), painPoints: lines(pForm.painPoints),
        objections: lines(pForm.objections), decisionFactors: lines(pForm.decisionFactors),
        searchBehavior: pForm.searchBehavior, languageStyle: pForm.languageStyle,
      } };
      const url = editingId
        ? `/api/domains/${activeDomainId}/personas/${editingId}`
        : `/api/domains/${activeDomainId}/personas`;
      const r = await fetch(url, {
        method: editingId ? "PUT" : "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? "Kaydedilemedi");
      if (!editingId) setPersonaIds((s) => new Set(s).add(data.id));
      setPersonaPanel(false); setEditingId(null); setPForm({ ...emptyForm });
      queryClient.invalidateQueries({ queryKey: ["personas", activeDomainId] });
    } catch (e) { setPersonaErr((e as Error).message); } finally { setPersonaBusy(false); }
  };
  const startEditPersona = (p: PersonaItem) => {
    setEditingId(p.id);
    setPForm({
      name: p.name, market: p.market ?? "", summary: p.data.summary ?? "", demographics: p.data.demographics ?? "", role: p.data.role ?? "",
      questions: (p.data.questions ?? []).join("\n"), painPoints: (p.data.painPoints ?? []).join("\n"),
      objections: (p.data.objections ?? []).join("\n"), decisionFactors: (p.data.decisionFactors ?? []).join("\n"),
      searchBehavior: p.data.searchBehavior ?? "", languageStyle: p.data.languageStyle ?? "",
    });
    setPersonaPanel(true); setPersonaErr(null);
  };
  const deletePersona = async (id: number) => {
    await fetch(`/api/domains/${activeDomainId}/personas/${id}`, { method: "DELETE" });
    setPersonaIds((s) => { const n = new Set(s); n.delete(id); return n; });
    queryClient.invalidateQueries({ queryKey: ["personas", activeDomainId] });
  };
  const selectedPersonas = (personas ?? []).filter((p) => personaIds.has(p.id));

  const { data: writerData } = useQuery({
    queryKey: ["writer-briefs", activeDomainId],
    enabled: !!activeDomainId,
    // yazar koşarken 5 sn'de bir yokla (asenkron motor — sonuç bitince kendiliğinden düşer)
    refetchInterval: (q) => ((q.state.data as { briefs?: WriterRow[] } | undefined)?.briefs ?? []).some((b) => b.running) ? 5000 : false,
    queryFn: async (): Promise<{ briefs: WriterRow[] }> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs`); return r.ok ? await r.json() : { briefs: [] }; } catch { return { briefs: [] }; }
    },
  });
  const writerBriefs = writerData?.briefs ?? [];

  // "Briefi Seç" akışı: kartta brief seçilir → panelde görünür → panel "İçerik Üret" üretir
  const [selectedBriefId, setSelectedBriefId] = useState<number | null>(null);
  const generate = async (briefId: number) => {
    if (!(await confirmCost("writer"))) return;
    await fetch(`/api/writer-briefs/${briefId}/generate`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ model: writerModel, tone, narrator, audience: "", wordTarget, imageCount, market: wMarket, personaIds: [...personaIds] }),
    });
    setSelectedBriefId(null);
    queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
  };
  // KELİMEDEN BAŞLAT (kullanıcı isteği): anahtar kelime yaz → AI+DFS araştırmasıyla brief
  // üretilir → seçili ayarlarla (ton, persona, kelime hedefi, pazar) içerik hemen yazılmaya başlar.
  const [kelime, setKelime] = useState("");
  const [kelimeBusy, setKelimeBusy] = useState(false);
  const kelimedenUret = async () => {
    if (!kelime.trim() || kelimeBusy || !activeDomainId) return;
    if (!(await confirmCost("writer"))) return;
    setKelimeBusy(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs/kelimeden`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ keyword: kelime.trim(), market: wMarket }),
      });
      const j = await r.json();
      if (r.status === 409 && j.id) {
        // Zaten havuzda — onu seç, kullanıcı Üret'e bassın
        setSelectedBriefId(j.id); setKelime("");
        queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
        return;
      }
      if (!r.ok) throw new Error(j.error ?? "Araştırma başarısız");
      // Üretimi hemen başlat (maliyet onayı yukarıda alındı — ikinci kez sorulmaz)
      await fetch(`/api/writer-briefs/${j.id}/generate`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ model: writerModel, tone, narrator, audience: "", wordTarget, imageCount, market: wMarket, personaIds: [...personaIds] }),
      });
      setKelime("");
      queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
    } catch (e) { alert(String(e instanceof Error ? e.message : e)); }
    finally { setKelimeBusy(false); }
  };
  const removeBrief = async (briefId: number) => {
    await fetch(`/api/writer-briefs/${briefId}`, { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["writer-briefs", activeDomainId] });
  };

  // İçerik Takvimi bağlantısı — yazılmış içerik tüm verisiyle (kapak dahil) plana düşer
  const [addedCal, setAddedCal] = useState<Record<number, boolean>>({});
  // Kalıcı durum sunucudan: sayfa yenilenince "Takvimde" ibaresi kaybolmasın.
  const takvimdeVar = useTakvimdeVar(activeDomainId);
  const takvimde = (w: WriterRow) => addedCal[w.id] || takvimdeVar(w.brief.targetKeyword);
  const addToCalendar = async (w: WriterRow) => {
    if (!activeDomainId) return;
    const r = w.result;
    const cover = r?.imageBriefs.find((im) => im.placeholder === "[GÖRSEL-1]" && im.imageUrl)?.imageUrl ?? null;
    await fetch(`/api/domains/${activeDomainId}/content-plans`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title: r?.title ?? w.brief.title,
        plannedDate: new Date().toISOString().slice(0, 10), contentType: "blog",
        coverUrl: cover ?? undefined,
        notes: r ? `${r.metaDescription}\n\nSlug: /${r.slug} · ${r.wordCount} kelime · denetçi ${r.audit?.score ?? "-"}/100\n(Yazar Robotu içeriği: /content/${w.id})` : w.brief.rationale,
        keywords: [{ keyword: w.brief.targetKeyword, usa: 0, global: w.brief.volume, role: "ana" }, ...(w.brief.supportingKeywords ?? []).slice(0, 6).map((k) => ({ keyword: k, usa: 0, global: 0, role: "destek" }))],
      }),
    });
    setAddedCal((p) => ({ ...p, [w.id]: true }));
  };

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      {/* Header — İçerik Üretim Ajanı */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-2xl dark:bg-brand-500/15">🤖</div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">İçerik Üretim Ajanı</h1>
              <span className="rounded-full bg-success-50 px-2 py-0.5 text-theme-xs font-medium text-success-600 dark:bg-success-500/15 dark:text-success-500">● Aktif</span>
            </div>
            <p className="text-sm text-muted-foreground">İçeriğiniz için en uygun üretim stratejisi hazırlanıyor</p>
          </div>
        </div>
      </div>

      {/* ══════════ YAZAR MOTORU — Robot brief'lerinden YAYINA HAZIR içerik ══════════ */}
      {tab === "yazar" && (<>
        {/* AJAN PANELİ — kullanıcının AI'a çizdirdiği tasarımın mevcut yapıya uyarlanması */}
        <Card className="mb-4 overflow-hidden"><CardContent className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px]">
            {/* Sol: ayarlar */}
            <div className="p-5 space-y-5">
              <AgentGroup icon="🎯" label="İçerik Stratejisi">
                {TONES.map((t) => (
                  <AgentOption key={t.k} active={tone === t.k} onClick={() => setTone(t.k)} title={t.d} label={t.l} />
                ))}
              </AgentGroup>

              <AgentGroup icon="🧠" label="Yapay Zeka Profili">
                <AgentOption active={writerModel === "opus"} onClick={() => setWriterModel("opus")} label="⚡ Hızlı" sub="Standart · ~2.000 token" title="Hızlı üretim — standart kalite" />
                <AgentOption active={writerModel === "fable"} onClick={() => setWriterModel("fable")} label="🎓 Uzman" sub="Uzman · ~3× token" title="Uzman motor — en üst seviye kalite" />
                <AgentOption active={narrator === "notr-uculcu"} onClick={() => setNarrator("notr-uculcu")} label="⚖️ Dengeli Ses" sub="nötr 3. şahıs" />
                <AgentOption active={narrator === "marka-biz"} onClick={() => setNarrator("marka-biz")} label="🎙️ Marka Sesi" sub="'biz' dili" />
              </AgentGroup>

              <AgentGroup icon="🌍" label="Dil ve Pazar">
                {(showAllLangs ? WRITER_MARKETS : WRITER_MARKETS.slice(0, 8)).map((m) => (
                  <AgentOption key={m.k} small active={wMarket === m.k} onClick={() => setWMarket(m.k)} label={m.l} />
                ))}
                {!showAllLangs && WRITER_MARKETS.length > 8 && (
                  <AgentOption small active={false} onClick={() => setShowAllLangs(true)} label={`＋ Daha fazla (${WRITER_MARKETS.length - 8})`} />
                )}
              </AgentGroup>

              <AgentGroup icon="🖼️" label="Görsel Kullanımı">
                <AgentOption active={imageCount === 0} onClick={() => setImageCount(0)} label="🚫 Görselsiz" />
                <AgentOption active={imageCount === 2} onClick={() => setImageCount(2)} label="🖼 Az Görsel" sub="kapak + 1" />
                <AgentOption active={imageCount === 3} onClick={() => setImageCount(3)} label="🏞 Dengeli" sub="kapak + 2" />
                <AgentOption active={imageCount === 6} onClick={() => setImageCount(6)} label="🎨 Görsel Ağırlıklı" sub="kapak + 5" />
              </AgentGroup>

              <AgentGroup icon="✍️" label="Kelime Hedefi">
                {[{ v: 500, l: "500" }, { v: 600, l: "600 ★" }, { v: 700, l: "700" }, { v: 1000, l: "1000" }, { v: 1500, l: "1500" }, { v: 0, l: "🤖 Otomatik" }].map((o) => (
                  <AgentOption key={o.v} small active={wordTarget === o.v} onClick={() => setWordTarget(o.v)} label={o.l} />
                ))}
              </AgentGroup>

              {/* HEDEF PERSONA — GEO'nun vazgeçilmezi: içerik tanımlı bir KİŞİYE yazılır */}
              <AgentGroup icon="🎭" label="Hedef Persona" hint="Birden fazla persona seçebilirsin — içerik hepsinin sorularını ve itirazlarını kapsar.">
                <AgentOption active={personaIds.size === 0} onClick={() => setPersonaIds(new Set())} label="🚫 Personasız" sub="genel kitle" />
                {(personas ?? []).map((p) => (
                  <AgentOption key={p.id} active={personaIds.has(p.id)} onClick={() => togglePersona(p.id)}
                    label={`${p.source === "auto" ? "🤖 " : "👤 "}${p.name.split("—")[0].trim()}${p.market ? ` · ${p.market.toUpperCase()}` : ""}`}
                    sub={(p.data.summary || p.name).slice(0, 44)} title={p.data.summary} check />
                ))}
                <AgentOption active={personaPanel && editingId === null} onClick={() => { setPersonaPanel((o) => !o); setEditingId(null); setPForm({ ...emptyForm }); setPersonaErr(null); }} label="＋ Persona Ekle" sub="otomatik / elle" />
              </AgentGroup>

              {selectedPersonas.length > 0 && !personaPanel && selectedPersonas.map((sp) => (
                <div key={sp.id} className="space-y-1.5 rounded-xl border border-brand-200 bg-brand-25 p-3.5 text-theme-xs dark:border-brand-500/30 dark:bg-brand-500/10">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-brand-600 dark:text-brand-400">{sp.name}</div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button onClick={() => startEditPersona(sp)} className="text-brand-600 hover:underline dark:text-brand-400" title="Tüm alanları düzenle">Düzenle</button>
                      <button onClick={() => deletePersona(sp.id)} className="text-red-400 hover:text-red-600" title="Personayı sil">Sil</button>
                    </div>
                  </div>
                  {sp.data.summary && <div className="text-muted-foreground">{sp.data.summary}</div>}
                  {(sp.data.questions ?? []).length > 0 && (
                    <div><span className="font-medium">Hedef sorular (GEO):</span> {(sp.data.questions ?? []).slice(0, 3).join(" · ")}{(sp.data.questions ?? []).length > 3 ? ` (+${(sp.data.questions ?? []).length - 3})` : ""}</div>
                  )}
                </div>
              ))}

              {personaPanel && (() => {
                const inp = {} as const; // dark uyumu: renkler artık sınıflarda
                return (
                <div className="space-y-3 rounded-xl border border-brand-200 bg-brand-25 p-4 dark:border-brand-500/30 dark:bg-brand-500/[0.07]">
                  {editingId === null && (<>
                    <div className="text-theme-xs font-semibold text-brand-600 dark:text-brand-400">🤖 Otomatik Persona — domain ve sektöre göre kurulur</div>
                    <div className="flex gap-2">
                      <input value={pAudience} onChange={(e) => setPAudience(e.target.value)} placeholder="Hedef kitleni tarif et (boş bırakırsan tamamen otomatik seçer)"
                        className={PERSONA_INPUT + " flex-1"} style={inp} />
                      <button onClick={generatePersonaAuto} disabled={personaBusy}
                        className="rounded-lg bg-brand-500 px-4 py-2 text-theme-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
                        {personaBusy ? "Üretiliyor…" : "Otomatik Oluştur"}
                      </button>
                    </div>
                  </>)}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="text-theme-xs font-semibold text-brand-600 dark:text-brand-400">
                      {editingId !== null ? "✏️ Personayı Düzenle — tüm alanlar" : "✍️ Elle Persona"}
                    </div>
                    <button onClick={() => void alanlariDoldur()} disabled={personaBusy}
                      title="Persona adını ve/veya özeti yaz — kalan alanları AI doldurur, boş olmayan alanlara dokunmaz. Kaydetmeden önce düzenleyebilirsin."
                      className="rounded-lg border border-brand-300 px-3 py-1.5 text-theme-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-60 dark:border-brand-500/40 dark:text-brand-400 dark:hover:bg-brand-500/10">
                      {personaBusy ? "Dolduruluyor…" : "✨ Alanları Otomatik Doldur"}
                    </button>
                  </div>
                  <div className="grid grid-cols-[1fr_120px] gap-2">
                    <FL l="Persona Adı"><input value={pForm.name} onChange={(e) => setF("name", e.target.value)} placeholder='Ör: "Ayşe — İlk kez saç ekimi araştıran"' className={PERSONA_INPUT} style={inp} /></FL>
                    <FL l="Pazar (tr/en/de…)"><input value={pForm.market} onChange={(e) => setF("market", e.target.value)} placeholder="tr" className={PERSONA_INPUT} style={inp} /></FL>
                  </div>
                  <FL l="Kim? (Özet)"><textarea value={pForm.summary} onChange={(e) => setF("summary", e.target.value)} rows={2} placeholder="1-2 cümle: yaş, rol, durum" className={PERSONA_TEXTAREA} style={inp} /></FL>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <FL l="Demografi"><input value={pForm.demographics} onChange={(e) => setF("demographics", e.target.value)} placeholder="Yaş / konum / gelir" className={PERSONA_INPUT} style={inp} /></FL>
                    <FL l="Meslek / Rol"><input value={pForm.role} onChange={(e) => setF("role", e.target.value)} placeholder="Ör: İK uzmanı, işletme sahibi" className={PERSONA_INPUT} style={inp} /></FL>
                  </div>
                  <FL l="Sorduğu Sorular — GEO Hedef Promptları (her satıra bir soru)"><textarea value={pForm.questions} onChange={(e) => setF("questions", e.target.value)} rows={4} placeholder={"ChatGPT/Google'a soracağı gerçek sorular"} className={PERSONA_TEXTAREA} style={inp} /></FL>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <FL l="Acı Noktaları (satır satır)"><textarea value={pForm.painPoints} onChange={(e) => setF("painPoints", e.target.value)} rows={3} placeholder={"Korkuları, dertleri"} className={PERSONA_TEXTAREA} style={inp} /></FL>
                    <FL l="İtirazları (satır satır)"><textarea value={pForm.objections} onChange={(e) => setF("objections", e.target.value)} rows={3} placeholder={"Satın alma/karar itirazları"} className={PERSONA_TEXTAREA} style={inp} /></FL>
                    <FL l="Karar Kriterleri (satır satır)"><textarea value={pForm.decisionFactors} onChange={(e) => setF("decisionFactors", e.target.value)} rows={3} placeholder={"Fiyat, güven, sonuç…"} className={PERSONA_TEXTAREA} style={inp} /></FL>
                    <FL l="Arama & AI Davranışı"><textarea value={pForm.searchBehavior} onChange={(e) => setF("searchBehavior", e.target.value)} rows={3} placeholder="Nasıl arar, neye tıklar, hangi platformlar" className={PERSONA_TEXTAREA} style={inp} /></FL>
                  </div>
                  <FL l="Ona Nasıl Yazılmalı"><textarea value={pForm.languageStyle} onChange={(e) => setF("languageStyle", e.target.value)} rows={2} placeholder="Dil seviyesi, ton, kaçınılacaklar" className={PERSONA_TEXTAREA} style={inp} /></FL>
                  <div className="flex items-center gap-3">
                    <button onClick={savePersonaManual} disabled={personaBusy}
                      className="rounded-lg bg-brand-600 px-4 py-2 text-theme-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60">
                      {editingId !== null ? "Değişiklikleri Kaydet" : "Kaydet"}
                    </button>
                    {editingId !== null && (
                      <button onClick={() => { setPersonaPanel(false); setEditingId(null); setPForm({ ...emptyForm }); }} className="text-sm text-muted-foreground hover:underline">Vazgeç</button>
                    )}
                    {personaErr && <div className="text-xs text-red-600">{personaErr}</div>}
                  </div>
                </div>
                );
              })()}

              {/* Hedef Kitle serbest metni kaldırıldı — Hedef Persona sistemi bu işi devraldı */}
            </div>

            {/* Sağ: Ajan Önerisi — seçimlerin canlı özeti */}
            <div className="flex flex-col border-t border-gray-100 bg-gray-25 p-5 lg:border-l lg:border-t-0 dark:border-gray-800 dark:bg-white/[0.02]">
              <div className="mb-3 flex items-center gap-1.5 text-theme-sm font-semibold text-brand-600 dark:text-brand-400">✨ Ajan Önerisi</div>
              <div className="space-y-2.5 text-xs flex-1">
                {[
                  `${TONES.find((t) => t.k === tone)?.l.replace(/^\S+\s/, "") ?? tone} tonu seçildi`,
                  wordTarget > 0 ? `${wordTarget} kelime hedefi seçildi` : "Kelime hedefi otomatik belirlenecek",
                  imageCount > 0 ? `${imageCount} görsel önerildi (ilki kapak; bölüm içeriğiyle birebir ilgili üretilir)` : "Görselsiz üretim",
                  `${WRITER_MARKETS.find((m) => m.k === wMarket)?.l ?? wMarket} pazarı için yazılacak`,
                  writerModel === "fable" ? "Uzman motorla en üst kalite" : "Standart motorla hızlı üretim",
                  selectedPersonas.length > 0 ? `🎭 ${selectedPersonas.length} persona: ${selectedPersonas.map((p) => p.name.split("—")[0].trim()).join(", ")} için yazılacak` : "Persona seçilmedi (genel kitle)",
                  ...(selectedPersonas.filter((p) => p.market && p.market.slice(0, 2).toLowerCase() !== wMarket.slice(0, 2).toLowerCase())
                    .map((p) => `⚠️ PAZAR UYUMSUZ: "${p.name.split("—")[0].trim()}" personası ${p.market!.toUpperCase()} pazarı için — içerik ${wMarket.toUpperCase()} pazarına yazılacak. Kontrol et.`)),
                  selectedBriefId ? `📋 Brief seçildi: "${(writerBriefs.find((w) => w.id === selectedBriefId)?.brief.title ?? "").slice(0, 50)}"` : "Brief seçilmedi — aşağıdan \"Briefi Seç\"e bas",
                ].filter(Boolean).map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success-500 text-[10px] text-white">✓</span>
                    <span className="text-muted-foreground">{s}</span>
                  </div>
                ))}
              </div>
              {selectedBriefId ? (
                <>
                  <div className="mt-4 rounded-xl border border-brand-200 bg-brand-25 p-3 text-theme-xs dark:border-brand-500/30 dark:bg-brand-500/10">
                    <div className="mb-0.5 font-semibold text-brand-600 dark:text-brand-400">📋 Seçili Brief</div>
                    <div className="text-muted-foreground">{writerBriefs.find((w) => w.id === selectedBriefId)?.brief.title}</div>
                  </div>
                  <button onClick={() => generate(selectedBriefId)}
                    className="mt-3 w-full rounded-xl bg-brand-500 py-3 text-theme-sm font-semibold text-white transition hover:bg-brand-600">
                    🚀 İçerik Üret
                  </button>
                  <button onClick={() => setSelectedBriefId(null)} className="mt-1.5 w-full text-[11px] text-muted-foreground hover:underline">Seçimi kaldır</button>
                </>
              ) : (
                <>
                  <button onClick={() => document.getElementById("brief-pool")?.scrollIntoView({ behavior: "smooth" })}
                    className="mt-4 w-full rounded-xl bg-brand-500 py-3 text-theme-sm font-semibold text-white transition hover:bg-brand-600">
                    📋 Brief Seç
                  </button>
                  <p className="text-[10px] text-muted-foreground mt-2 text-center">Aşağıdaki havuzdan "Briefi Seç"e bas — brief burada görünür, sonra İçerik Üret.</p>
                </>
              )}
              {/* KELİMEDEN BAŞLAT: brief havuzunu beklemeden anahtar kelimeyle tek adımda üretim */}
              <div className="mt-4 rounded-xl border border-dashed p-3" style={{ borderColor: "rgba(232,62,60,0.4)" }}>
                <div className="mb-1 text-theme-xs font-semibold text-brand-600 dark:text-brand-400">🔍 Kelimeden Başlat</div>
                <p className="mb-2 text-[10.5px] text-muted-foreground">Anahtar kelimeyi yaz — hacim/zorluk araştırılır, başlık planı kurulur ve yukarıdaki ayarlarla (ton, persona, kelime hedefi, {wMarket.toUpperCase()} pazarı) içerik hemen yazılmaya başlar.</p>
                <input value={kelime} onChange={(e) => setKelime(e.target.value)} placeholder="örn: boşanma davası ne kadar sürer"
                  onKeyDown={(e) => { if (e.key === "Enter") void kelimedenUret(); }} disabled={kelimeBusy}
                  className="mb-2 w-full rounded-lg border bg-transparent px-3 py-2 text-[12.5px] outline-none dark:border-white/15" />
                <button onClick={() => void kelimedenUret()} disabled={kelimeBusy || !kelime.trim()}
                  className="w-full rounded-xl border border-brand-500 py-2.5 text-theme-xs font-semibold text-brand-600 transition hover:bg-brand-50 disabled:opacity-60 dark:text-brand-400 dark:hover:bg-brand-500/10">
                  {kelimeBusy ? "Araştırılıyor…" : "🚀 Araştır ve Üret"}
                </button>
              </div>
            </div>
          </div>
        </CardContent></Card>

        {/* Brief havuzu */}
        {writerBriefs.length === 0 && (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Havuz boş. <b>Brief Keşif Ajanı</b>'nda beğendiğin brief'in altındaki <b>"✍️ Üretim Ajanına Gönder"</b> butonuyla buraya ekle.
          </CardContent></Card>
        )}
        <div className="space-y-3" id="brief-pool">
          {writerBriefs.map((w) => {
            const r = w.result;
            return (
              <Card key={w.id} style={w.running ? { borderColor: `${C.cyan}66` } : undefined}><CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  {(() => { const cov = r?.imageBriefs.find((im) => im.placeholder === "[GÖRSEL-1]" && im.imageUrl); return cov ? (
                    <Link href={`/content/${w.id}`}><img src={cov.imageUrl!} alt={cov.altText} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="rounded-lg object-cover shrink-0 cursor-pointer" style={{ width: 96, height: 60 }} /></Link>
                  ) : null; })()}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full" style={w.status === "yazildi" ? { background: "rgba(34,197,94,0.12)", color: "#039855" } : w.running ? { background: `${C.cyan}18`, color: "#b12220" } : { background: "rgba(0,0,0,0.05)", color: "#70706c" }}>
                        {w.running ? "⏳ yazılıyor…" : w.status === "yazildi" ? "✅ yazıldı" : "📋 beklemede"}
                      </span>
                      {w.brief.intentKind && <span className="rounded-full bg-blue-light-50 px-2 py-0.5 text-theme-xs text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-500">{w.brief.intentKind === "ticari" ? "💼 Ticari" : "📖 Bilgi"}</span>}
                      {w.brief.clusterName && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-theme-xs text-gray-600 dark:bg-white/5 dark:text-gray-300">{w.brief.clusterName}</span>}
                      {r?.audit && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: r.audit.score >= 80 ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)", color: r.audit.score >= 80 ? "#039855" : "#b54708" }}>🛡️ {r.audit.score}/100{r.audit.fixedIssues > 0 ? ` · ${r.audit.fixedIssues} düzeltildi` : ""}</span>}
                    </div>
                    {/* Başlık → Keşif Ajanı'ndaki detay sayfası (varyasyon tablosu, semantik set, derin analiz…) */}
                    <Link href={`/planner-brief/${encodeURIComponent(w.brief.targetKeyword)}`}>
                      <div className="font-semibold text-sm hover:underline cursor-pointer" title="Brief detayını aç (varyasyon tablosu, semantik set, plan, derin analiz)">{r?.title ?? w.brief.title}</div>
                    </Link>
                    <div className="text-xs mt-0.5"><Target className="w-3 h-3 inline mr-1" style={{ color: C.amber }} /><b style={{ color: C.amber }}>{w.brief.targetKeyword}</b>{w.brief.volume > 0 && <span className="text-muted-foreground"> · {fmt(w.brief.volume)}/ay</span>}{r && <span className="text-muted-foreground"> · {fmt(r.wordCount)} kelime · {r.imageBriefs.length} görsel · {r.linkPlan.length} link</span>}
                      <Link href={`/planner-brief/${encodeURIComponent(w.brief.targetKeyword)}`}><span className="ml-2 cursor-pointer text-brand-500 hover:underline">Detay →</span></Link>
                    </div>
                    {w.runError && <div className="mt-1 text-theme-xs text-error-600 dark:text-error-400">⚠️ {w.runError} — tekrar dene.</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Yeniden üretim detay sayfasına taşındı — listede yalnız ilk üretim */}
                    {!w.running && w.status !== "yazildi" && (
                      selectedBriefId === w.id ? (
                        <Button size="sm" variant="outline" onClick={() => setSelectedBriefId(null)} className="gap-1.5 border-brand-300 text-theme-xs text-brand-600 dark:text-brand-400">
                          <Check className="w-3.5 h-3.5" /> Seçildi — motora bak ↑
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => { setSelectedBriefId(w.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="gap-1.5 text-xs" style={{ background: C.cyan, color: "#141412" }}>
                          <Sparkles className="w-3.5 h-3.5" /> Briefi Seç
                        </Button>
                      )
                    )}
                    {w.running && <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.cyan }} />}
                    {r && (
                      <Button size="sm" variant={takvimde(w) ? "outline" : "secondary"} disabled={takvimde(w)} className="text-xs gap-1" onClick={() => addToCalendar(w)}>
                        {takvimde(w) ? <><Check className="w-3.5 h-3.5" style={{ color: C.green }} /> Takvimde</> : <><CalendarPlus className="w-3.5 h-3.5" /> Takvime Ekle</>}
                      </Button>
                    )}
                    {r && <Link href={`/content/${w.id}`}><Button size="sm" variant="ghost" className="text-xs">İçeriği Gör →</Button></Link>}
                    <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={() => removeBrief(w.id)}>Sil</Button>
                  </div>
                </div>

                {/* İçerik detayı artık ayrı sayfada: /content/:id */}
              </CardContent></Card>
            );
          })}
        </div>
      </>)}

      {/* İçerik Fırsatları ve AI İyileştirmeleri sekmeleri kullanıcı isteğiyle kaldırıldı — bu sayfa yalnız Yazar Motoru. */}
    </div>
  );
}
